import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  Pressable,
  ActivityIndicator,
  TextInput,
  StyleSheet,
  Alert,
} from 'react-native';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';

import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AppButton from '../components/AppButton';
import ToggleSwitch from '../components/ToggleSwitch';
import Util from '../util/util.js';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

/**
 * MonetaFeatureToggles - Moneta (super-admin) screen to manage per-org feature toggles.
 *
 * Feature-centric: pick a feature to expand it, then turn it on/off for individual orgs.
 * A newly created feature is OFF for every org until explicitly enabled here.
 */
const MonetaFeatureToggles = ({ route }) => {
  const { t } = useTranslation(['admin', 'common']);

  const { containerStyle } = useResponsiveStyles();
  const { organization, admin, userAdmin, orgAdmin } = route.params || {};

  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  // Create-feature form
  const [showForm, setShowForm] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  // Expanded feature + its enabled-org set
  const [expandedKey, setExpandedKey] = useState(null);
  const [enabledOrgs, setEnabledOrgs] = useState({}); // { [orgName]: true }
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [pendingOrg, setPendingOrg] = useState(null); // org currently being toggled

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [featuresResult, orgsResult] = await Promise.allSettled([
        Util.get('Resty', '/items/feature-toggles'),
        Util.get('Resty', '/items/organizations/all'),
      ]);

      if (featuresResult.status === 'fulfilled' && featuresResult.value && !featuresResult.value.error) {
        setFeatures(featuresResult.value.items || []);
      } else {
        console.log('Features fetch failed:', featuresResult.reason || featuresResult.value?.error);
      }

      if (orgsResult.status === 'fulfilled' && orgsResult.value && !orgsResult.value.error) {
        setOrganizations(orgsResult.value.items || []);
      } else {
        console.log('Orgs fetch failed:', orgsResult.reason || orgsResult.value?.error);
      }
    } catch (error) {
      console.log('Error loading feature toggles:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const createFeature = async () => {
    const key = newKey.trim();
    const name = newName.trim();
    if (!/^[a-z0-9][a-z0-9-]{0,63}$/.test(key)) {
      Alert.alert(t('featureToggles.invalidKeyTitle'), t('featureToggles.invalidKeyMessage'));
      return;
    }
    if (!name) {
      Alert.alert(t('featureToggles.nameRequiredTitle'), t('featureToggles.nameRequiredMessage'));
      return;
    }

    try {
      setCreating(true);
      const result = await Util.post('Resty', '/items/feature-toggles', {
        body: { key, name, description: newDescription.trim() },
      });
      if (result?.error) {
        Alert.alert(t('featureToggles.createFailed'), String(result.error));
        return;
      }
      setNewKey('');
      setNewName('');
      setNewDescription('');
      setShowForm(false);
      await loadData();
    } catch (error) {
      Alert.alert(t('featureToggles.createFailed'), error.message || String(error));
    } finally {
      setCreating(false);
    }
  };

  const deleteFeature = (key) => {
    Alert.alert(
      t('featureToggles.deleteConfirmTitle'),
      t('featureToggles.deleteConfirmMessage', { key }),
      [
        { text: t('common:actions.cancel'), style: 'cancel' },
        {
          text: t('common:actions.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await Util.del('Resty', '/items/feature-toggles', { queryParams: { key } });
              if (expandedKey === key) setExpandedKey(null);
              await loadData();
            } catch (error) {
              Alert.alert(t('featureToggles.deleteFailed'), error.message || String(error));
            }
          },
        },
      ]
    );
  };

  const toggleFeatureExpanded = async (key) => {
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    setEnabledOrgs({});
    try {
      setOrgsLoading(true);
      const result = await Util.get('Resty', '/items/feature-toggles/orgs', {
        queryParams: { key },
      });
      const map = {};
      (result?.items || []).forEach((org) => {
        map[org] = true;
      });
      setEnabledOrgs(map);
    } catch (error) {
      console.log('Error loading enabled orgs:', error);
    } finally {
      setOrgsLoading(false);
    }
  };

  const toggleOrg = async (key, orgName, nextEnabled) => {
    // Optimistic update with rollback on failure.
    setPendingOrg(orgName);
    setEnabledOrgs((prev) => ({ ...prev, [orgName]: nextEnabled }));
    try {
      if (nextEnabled) {
        await Util.post('Resty', '/items/feature-toggles/org', {
          body: { key, organization: orgName },
        });
      } else {
        await Util.del('Resty', '/items/feature-toggles/org', {
          queryParams: { key, organization: orgName },
        });
      }
    } catch (error) {
      setEnabledOrgs((prev) => ({ ...prev, [orgName]: !nextEnabled }));
      Alert.alert(t('featureToggles.toggleFailed'), error.message || String(error));
    } finally {
      setPendingOrg(null);
    }
  };

  const enabledCount = (key) => (expandedKey === key ? Object.values(enabledOrgs).filter(Boolean).length : null);

  return (
    <View style={[containerStyle, styles.container]}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.pageHeader}>
          <CustomText font="semiBold" size={24} color={AppStyles.colorSet.textPrimary}>
            {t('featureToggles.title')}
          </CustomText>
          <CustomText
            font="regular"
            size={14}
            color={AppStyles.colorSet.textSecondary}
            style={styles.pageSubtitle}
          >
            {t('featureToggles.description')}
          </CustomText>
        </View>

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={AppStyles.colorSet.brandPrimary} />
          </View>
        ) : (
          <>
            {/* Create feature */}
            <View style={styles.section}>
              {showForm ? (
                <View style={styles.formCard}>
                  <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary} style={styles.formTitle}>
                    {t('featureToggles.newFeature')}
                  </CustomText>
                  <CustomText font="medium" size={13} color={AppStyles.colorSet.textSecondary}>
                    {t('featureToggles.keyLabel')}
                  </CustomText>
                  <TextInput
                    style={styles.input}
                    placeholder={t('featureToggles.keyPlaceholder')}
                    autoCapitalize="none"
                    autoCorrect={false}
                    value={newKey}
                    onChangeText={setNewKey}
                  />
                  <CustomText font="medium" size={13} color={AppStyles.colorSet.textSecondary}>
                    {t('featureToggles.nameLabel')}
                  </CustomText>
                  <TextInput
                    style={styles.input}
                    placeholder={t('featureToggles.namePlaceholder')}
                    value={newName}
                    onChangeText={setNewName}
                  />
                  <CustomText font="medium" size={13} color={AppStyles.colorSet.textSecondary}>
                    {t('featureToggles.descriptionLabel')}
                  </CustomText>
                  <TextInput
                    style={[styles.input, styles.inputMultiline]}
                    placeholder={t('featureToggles.descriptionPlaceholder')}
                    value={newDescription}
                    onChangeText={setNewDescription}
                    multiline
                  />
                  <View style={styles.formActions}>
                    <AppButton
                      title={t('common:actions.cancel')}
                      variant="secondary"
                      onPress={() => setShowForm(false)}
                      isEnabled={!creating}
                      buttonStyle={styles.formActionButton}
                    />
                    <AppButton
                      title={t('featureToggles.create')}
                      variant="primary"
                      onPress={createFeature}
                      isBusy={creating}
                      isEnabled={!creating}
                      buttonStyle={styles.formActionButton}
                    />
                  </View>
                </View>
              ) : (
                <Pressable
                  style={({ hovered, focused, pressed }) => [
                    styles.addButton,
                    { cursor: 'pointer' },
                    (hovered || focused) && styles.addButtonHovered,
                    pressed && { opacity: 0.85 },
                  ]}
                  onPress={() => setShowForm(true)}
                >
                  <CustomText font="semiBold" size={15} color={AppStyles.colorSet.textPrimary}>
                    {t('featureToggles.addNew')}
                  </CustomText>
                </Pressable>
              )}
            </View>

            {/* Feature list */}
            <View style={styles.section}>
              <CustomText font="semiBold" size={18} color={AppStyles.colorSet.textPrimary} style={styles.sectionTitle}>
                {t('featureToggles.featuresCount', { count: features.length })}
              </CustomText>

              {features.length === 0 ? (
                <View style={styles.emptyState}>
                  <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
                    {t('featureToggles.empty')}
                  </CustomText>
                </View>
              ) : (
                features.map((feature) => {
                  const isExpanded = expandedKey === feature.key;
                  const count = enabledCount(feature.key);
                  return (
                    <View key={feature.key} style={styles.featureCard}>
                      <Pressable
                        style={({ hovered, focused }) => [
                          styles.featureHeader,
                          { cursor: 'pointer' },
                          (hovered || focused) && styles.featureHeaderHovered,
                        ]}
                        onPress={() => toggleFeatureExpanded(feature.key)}
                      >
                        <View style={styles.featureHeaderText}>
                          <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
                            {feature.name}
                          </CustomText>
                          <CustomText font="regular" size={12} color={AppStyles.colorSet.textTertiary}>
                            {feature.key}
                            {count !== null ? t('featureToggles.onForOrgs', { count }) : ''}
                          </CustomText>
                          {!!feature.description && (
                            <CustomText
                              font="regular"
                              size={13}
                              color={AppStyles.colorSet.textSecondary}
                              style={{ marginTop: 4 }}
                            >
                              {feature.description}
                            </CustomText>
                          )}
                        </View>
                        <CustomText font="regular" size={18} color={AppStyles.colorSet.textTertiary}>
                          {isExpanded ? '▾' : '▸'}
                        </CustomText>
                      </Pressable>

                      {isExpanded && (
                        <View style={styles.orgList}>
                          {orgsLoading ? (
                            <ActivityIndicator color={AppStyles.colorSet.brandPrimary} style={{ paddingVertical: 12 }} />
                          ) : organizations.length === 0 ? (
                            <CustomText font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
                              {t('featureToggles.noOrgs')}
                            </CustomText>
                          ) : (
                            organizations.map((org) => (
                              <View key={org.organization} style={styles.orgRow}>
                                <CustomText font="regular" size={14} color={AppStyles.colorSet.textPrimary}>
                                  {org.organization}
                                </CustomText>
                                <ToggleSwitch
                                  small
                                  value={!!enabledOrgs[org.organization]}
                                  disabled={pendingOrg === org.organization}
                                  onValueChange={(next) => toggleOrg(feature.key, org.organization, next)}
                                />
                              </View>
                            ))
                          )}

                          <Pressable
                            style={{ ...styles.deleteLink, cursor: 'pointer' }}
                            onPress={() => deleteFeature(feature.key)}
                          >
                            {({ hovered, focused }) => (
                              <CustomText
                                font="medium"
                                size={13}
                                color={AppStyles.colorSet.errorColor || '#CC3333'}
                                style={(hovered || focused) && { textDecorationLine: 'underline' }}
                              >
                                {t('featureToggles.deleteFeature')}
                              </CustomText>
                            )}
                          </Pressable>
                        </View>
                      )}
                    </View>
                  );
                })
              )}
            </View>
          </>
        )}
      </ScrollView>
      <Footer organization={organization} admin={admin} userAdmin={userAdmin} orgAdmin={orgAdmin} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    paddingBottom: 40,
    flexGrow: 1,
    alignItems: 'stretch',
  },
  pageHeader: {
    paddingHorizontal: 16,
    paddingTop: hp(2),
    paddingBottom: hp(1),
  },
  pageSubtitle: {
    marginTop: 4,
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  section: {
    paddingHorizontal: 16,
    marginBottom: hp(1),
  },
  sectionTitle: {
    marginBottom: 12,
  },
  addButton: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
  },
  // Web hover/focus feedback — lift on the filled New Feature button
  addButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  formCard: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
  },
  formTitle: {
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginTop: 6,
    marginBottom: 12,
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
    backgroundColor: '#FFFFFF',
  },
  inputMultiline: {
    height: 70,
    textAlignVertical: 'top',
  },
  formActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  formActionButton: {
    flex: 1,
  },
  featureCard: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    overflow: 'hidden',
  },
  featureHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  // Web hover/focus feedback for the expandable feature card header
  featureHeaderHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  featureHeaderText: {
    flex: 1,
    paddingRight: 12,
  },
  orgList: {
    borderTopWidth: 1,
    borderTopColor: AppStyles.colorSet.cardBorder,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  orgRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F1F3',
  },
  deleteLink: {
    paddingVertical: 12,
    alignItems: 'flex-start',
  },
  emptyState: {
    paddingVertical: 24,
    alignItems: 'center',
  },
});

export default MonetaFeatureToggles;
